/**
 * Multi-language support for the accessibility statement page.
 *
 * Phase 1: Translates UI labels surrounding the generated statement.
 * The actual statement HTML content is still generated in English server-side.
 * Future phases will add server-side statement generation in each language.
 */

export const STATEMENT_LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "fr", label: "French", nativeLabel: "Français" },
  { code: "de", label: "German", nativeLabel: "Deutsch" },
  { code: "es", label: "Spanish", nativeLabel: "Español" },
  { code: "it", label: "Italian", nativeLabel: "Italiano" },
  { code: "nl", label: "Dutch", nativeLabel: "Nederlands" },
  { code: "pt", label: "Portuguese", nativeLabel: "Português" },
  { code: "ga", label: "Irish", nativeLabel: "Gaeilge" },
  { code: "cy", label: "Welsh", nativeLabel: "Cymraeg" },
  { code: "sv", label: "Swedish", nativeLabel: "Svenska" },
  { code: "da", label: "Danish", nativeLabel: "Dansk" },
  { code: "pl", label: "Polish", nativeLabel: "Polski" },
] as const;

export type StatementLanguageCode =
  (typeof STATEMENT_LANGUAGES)[number]["code"];

/** UI labels translations — only the parts we control, not the generated HTML. */
export const STATEMENT_UI_LABELS: Record<
  StatementLanguageCode,
  {
    title: string;
    generatedOn: string;
    summary: string;
    wcagLevel: string;
    complianceStatus: string;
    score: string;
    manualReview: string;
    organisation: string;
    coverage: string;
    pagesScanned: string;
    pagesDiscovered: string;
    coveragePercent: string;
    templatesScanned: string;
    yourStatement: string;
    copyHtml: string;
    copied: string;
    downloadNotice: string;
    legalDisclaimer: string;
    languageNotice: string;
    languageSelectorLabel: string;
    accessibilityBadge: string;
    interactiveWidget: string;
    fullReport: string;
    dashboard: string;
    runScan: string;
    noStatementYet: string;
  }
> = {
  en: {
    title: "Accessibility Statement",
    generatedOn: "Generated",
    summary: "Summary",
    wcagLevel: "WCAG level",
    complianceStatus: "Compliance status",
    score: "Score",
    manualReview: "Manual review",
    organisation: "Organisation",
    coverage: "Coverage",
    pagesScanned: "Pages scanned",
    pagesDiscovered: "Pages discovered",
    coveragePercent: "Coverage",
    templatesScanned: "Templates scanned",
    yourStatement: "Your accessibility statement",
    copyHtml: "Copy statement HTML",
    copied: "Copied!",
    downloadNotice:
      "This is the accessibility statement generated from your latest scan. Copy the HTML below and paste it into a page on your website.",
    legalDisclaimer:
      "This statement reflects automated scan results only and is not a legal declaration of full WCAG conformance.",
    languageNotice: "",
    languageSelectorLabel: "Statement language",
    accessibilityBadge: "Accessibility badge",
    interactiveWidget: "Interactive widget",
    fullReport: "Full report",
    dashboard: "Dashboard",
    runScan: "Run a scan",
    noStatementYet:
      "No statement has been generated yet. Run a scan and your accessibility statement will be created automatically.",
  },
  fr: {
    title: "Déclaration d'accessibilité",
    generatedOn: "Généré le",
    summary: "Résumé",
    wcagLevel: "Niveau WCAG",
    complianceStatus: "Statut de conformité",
    score: "Score",
    manualReview: "Examen manuel",
    organisation: "Organisation",
    coverage: "Couverture",
    pagesScanned: "Pages analysées",
    pagesDiscovered: "Pages découvertes",
    coveragePercent: "Couverture",
    templatesScanned: "Modèles analysés",
    yourStatement: "Votre déclaration d'accessibilité",
    copyHtml: "Copier le HTML",
    copied: "Copié !",
    downloadNotice:
      "Déclaration d'accessibilité générée à partir de votre dernière analyse. Copiez le HTML ci-dessous et collez-le dans votre site web.",
    legalDisclaimer:
      "Cette déclaration reflète uniquement les résultats d'analyse automatique et ne constitue pas une déclaration légale de conformité WCAG.",
    languageNotice:
      "Note : le contenu de la déclaration est actuellement généré en anglais. Les déclarations traduites seront bientôt disponibles.",
    languageSelectorLabel: "Langue de la déclaration",
    accessibilityBadge: "Badge d'accessibilité",
    interactiveWidget: "Widget interactif",
    fullReport: "Rapport complet",
    dashboard: "Tableau de bord",
    runScan: "Lancer une analyse",
    noStatementYet:
      "Aucune déclaration n'a encore été générée. Lancez une analyse et votre déclaration d'accessibilité sera créée automatiquement.",
  },
  de: {
    title: "Erklärung zur Barrierefreiheit",
    generatedOn: "Erstellt am",
    summary: "Zusammenfassung",
    wcagLevel: "WCAG-Stufe",
    complianceStatus: "Konformitätsstatus",
    score: "Punktzahl",
    manualReview: "Manuelle Prüfung",
    organisation: "Organisation",
    coverage: "Abdeckung",
    pagesScanned: "Geprüfte Seiten",
    pagesDiscovered: "Entdeckte Seiten",
    coveragePercent: "Abdeckung",
    templatesScanned: "Geprüfte Vorlagen",
    yourStatement: "Ihre Barrierefreiheitserklärung",
    copyHtml: "HTML kopieren",
    copied: "Kopiert!",
    downloadNotice:
      "Diese Barrierefreiheitserklärung wurde aus Ihrem letzten Scan erstellt. Kopieren Sie das HTML und fügen Sie es in Ihre Website ein.",
    legalDisclaimer:
      "Diese Erklärung spiegelt nur automatisierte Scanergebnisse wider und stellt keine rechtliche Erklärung zur WCAG-Konformität dar.",
    languageNotice:
      "Hinweis: Der Inhalt der Erklärung wird derzeit auf Englisch generiert. Übersetzte Erklärungen folgen in Kürze.",
    languageSelectorLabel: "Sprache der Erklärung",
    accessibilityBadge: "Barrierefreiheits-Badge",
    interactiveWidget: "Interaktives Widget",
    fullReport: "Vollständiger Bericht",
    dashboard: "Dashboard",
    runScan: "Scan starten",
    noStatementYet:
      "Es wurde noch keine Erklärung erstellt. Starten Sie einen Scan und Ihre Barrierefreiheitserklärung wird automatisch erstellt.",
  },
  es: {
    title: "Declaración de accesibilidad",
    generatedOn: "Generado el",
    summary: "Resumen",
    wcagLevel: "Nivel WCAG",
    complianceStatus: "Estado de conformidad",
    score: "Puntuación",
    manualReview: "Revisión manual",
    organisation: "Organización",
    coverage: "Cobertura",
    pagesScanned: "Páginas analizadas",
    pagesDiscovered: "Páginas descubiertas",
    coveragePercent: "Cobertura",
    templatesScanned: "Plantillas analizadas",
    yourStatement: "Su declaración de accesibilidad",
    copyHtml: "Copiar HTML",
    copied: "¡Copiado!",
    downloadNotice:
      "Declaración de accesibilidad generada a partir de su último análisis. Copie el HTML y péguelo en su sitio web.",
    legalDisclaimer:
      "Esta declaración refleja solo los resultados del análisis automatizado y no es una declaración legal de conformidad WCAG.",
    languageNotice:
      "Nota: el contenido de la declaración se genera actualmente en inglés. Las declaraciones traducidas estarán disponibles próximamente.",
    languageSelectorLabel: "Idioma de la declaración",
    accessibilityBadge: "Insignia de accesibilidad",
    interactiveWidget: "Widget interactivo",
    fullReport: "Informe completo",
    dashboard: "Panel de control",
    runScan: "Ejecutar análisis",
    noStatementYet:
      "Aún no se ha generado ninguna declaración. Ejecute un análisis y su declaración de accesibilidad se creará automáticamente.",
  },
  it: {
    title: "Dichiarazione di accessibilità",
    generatedOn: "Generato il",
    summary: "Riepilogo",
    wcagLevel: "Livello WCAG",
    complianceStatus: "Stato di conformità",
    score: "Punteggio",
    manualReview: "Revisione manuale",
    organisation: "Organizzazione",
    coverage: "Copertura",
    pagesScanned: "Pagine analizzate",
    pagesDiscovered: "Pagine scoperte",
    coveragePercent: "Copertura",
    templatesScanned: "Modelli analizzati",
    yourStatement: "La tua dichiarazione di accessibilità",
    copyHtml: "Copia HTML",
    copied: "Copiato!",
    downloadNotice:
      "Dichiarazione di accessibilità generata dall'ultima analisi. Copia l'HTML e incollalo nel tuo sito web.",
    legalDisclaimer:
      "Questa dichiarazione riflette solo i risultati dell'analisi automatica e non è una dichiarazione legale di conformità WCAG.",
    languageNotice:
      "Nota: il contenuto della dichiarazione è attualmente generato in inglese. Le dichiarazioni tradotte saranno disponibili a breve.",
    languageSelectorLabel: "Lingua della dichiarazione",
    accessibilityBadge: "Badge di accessibilità",
    interactiveWidget: "Widget interattivo",
    fullReport: "Rapporto completo",
    dashboard: "Dashboard",
    runScan: "Esegui analisi",
    noStatementYet:
      "Nessuna dichiarazione è stata ancora generata. Esegui un'analisi e la tua dichiarazione di accessibilità sarà creata automaticamente.",
  },
  nl: {
    title: "Toegankelijkheidsverklaring",
    generatedOn: "Gegenereerd op",
    summary: "Samenvatting",
    wcagLevel: "WCAG-niveau",
    complianceStatus: "Nalevingsstatus",
    score: "Score",
    manualReview: "Handmatige beoordeling",
    organisation: "Organisatie",
    coverage: "Dekking",
    pagesScanned: "Gescande pagina's",
    pagesDiscovered: "Ontdekte pagina's",
    coveragePercent: "Dekking",
    templatesScanned: "Gescande sjablonen",
    yourStatement: "Uw toegankelijkheidsverklaring",
    copyHtml: "HTML kopiëren",
    copied: "Gekopieerd!",
    downloadNotice:
      "Toegankelijkheidsverklaring gegenereerd op basis van uw laatste scan. Kopieer de HTML en plak deze in uw website.",
    legalDisclaimer:
      "Deze verklaring weerspiegelt alleen geautomatiseerde scanresultaten en is geen wettelijke verklaring van WCAG-conformiteit.",
    languageNotice:
      "Opmerking: de inhoud van de verklaring wordt momenteel in het Engels gegenereerd. Vertaalde verklaringen volgen binnenkort.",
    languageSelectorLabel: "Taal van de verklaring",
    accessibilityBadge: "Toegankelijkheidsbadge",
    interactiveWidget: "Interactieve widget",
    fullReport: "Volledig rapport",
    dashboard: "Dashboard",
    runScan: "Scan uitvoeren",
    noStatementYet:
      "Er is nog geen verklaring gegenereerd. Voer een scan uit en uw toegankelijkheidsverklaring wordt automatisch aangemaakt.",
  },
  pt: {
    title: "Declaração de acessibilidade",
    generatedOn: "Gerado em",
    summary: "Resumo",
    wcagLevel: "Nível WCAG",
    complianceStatus: "Estado de conformidade",
    score: "Pontuação",
    manualReview: "Revisão manual",
    organisation: "Organização",
    coverage: "Cobertura",
    pagesScanned: "Páginas analisadas",
    pagesDiscovered: "Páginas descobertas",
    coveragePercent: "Cobertura",
    templatesScanned: "Modelos analisados",
    yourStatement: "A sua declaração de acessibilidade",
    copyHtml: "Copiar HTML",
    copied: "Copiado!",
    downloadNotice:
      "Declaração de acessibilidade gerada a partir da sua última análise. Copie o HTML e cole-o no seu site.",
    legalDisclaimer:
      "Esta declaração reflete apenas os resultados da análise automatizada e não é uma declaração legal de conformidade WCAG.",
    languageNotice:
      "Nota: o conteúdo da declaração é atualmente gerado em inglês. Declarações traduzidas estarão disponíveis em breve.",
    languageSelectorLabel: "Idioma da declaração",
    accessibilityBadge: "Badge de acessibilidade",
    interactiveWidget: "Widget interativo",
    fullReport: "Relatório completo",
    dashboard: "Painel de controlo",
    runScan: "Executar análise",
    noStatementYet:
      "Ainda não foi gerada nenhuma declaração. Execute uma análise e a sua declaração de acessibilidade será criada automaticamente.",
  },
  ga: {
    title: "Ráiteas Inrochtaineachta",
    generatedOn: "Ginte ar",
    summary: "Achoimre",
    wcagLevel: "Leibhéal WCAG",
    complianceStatus: "Stádas comhlíonta",
    score: "Scór",
    manualReview: "Athbhreithniú láimhe",
    organisation: "Eagraíocht",
    coverage: "Clúdach",
    pagesScanned: "Leathanaigh scanáilte",
    pagesDiscovered: "Leathanaigh aimsithe",
    coveragePercent: "Clúdach",
    templatesScanned: "Teimpléid scanáilte",
    yourStatement: "Do ráiteas inrochtaineachta",
    copyHtml: "Cóipeáil HTML",
    copied: "Cóipeáilte!",
    downloadNotice:
      "Ráiteas inrochtaineachta ginte ón scanadh is déanaí. Cóipeáil an HTML agus greamaigh ar do shuíomh gréasáin é.",
    legalDisclaimer:
      "Léiríonn an ráiteas seo torthaí scanadh uathoibríoch amháin agus ní dearbhú dlíthiúil comhlíontachta WCAG é.",
    languageNotice:
      "Nóta: gintear ábhar an ráitis i mBéarla faoi láthair. Beidh ráitis aistrithe ar fáil go luath.",
    languageSelectorLabel: "Teanga an ráitis",
    accessibilityBadge: "Suaitheantas inrochtaineachta",
    interactiveWidget: "Giuirléid idirghníomhach",
    fullReport: "Tuairisc iomlán",
    dashboard: "Deais",
    runScan: "Rith scanadh",
    noStatementYet:
      "Níl aon ráiteas ginte fós. Rith scanadh agus cruthófar do ráiteas inrochtaineachta go huathoibríoch.",
  },
  cy: {
    title: "Datganiad Hygyrchedd",
    generatedOn: "Cynhyrchwyd ar",
    summary: "Crynodeb",
    wcagLevel: "Lefel WCAG",
    complianceStatus: "Statws cydymffurfio",
    score: "Sgôr",
    manualReview: "Adolygiad llaw",
    organisation: "Sefydliad",
    coverage: "Cwmpas",
    pagesScanned: "Tudalennau a sganiwyd",
    pagesDiscovered: "Tudalennau a ddarganfuwyd",
    coveragePercent: "Cwmpas",
    templatesScanned: "Templedi a sganiwyd",
    yourStatement: "Eich datganiad hygyrchedd",
    copyHtml: "Copïo HTML",
    copied: "Wedi copïo!",
    downloadNotice:
      "Datganiad hygyrchedd a gynhyrchwyd o'ch sgan diweddaraf. Copïwch y HTML a'i ludo ar eich gwefan.",
    legalDisclaimer:
      "Mae'r datganiad hwn yn adlewyrchu canlyniadau sgan awtomatig yn unig ac nid yw'n ddatganiad cyfreithiol o gydymffurfiad WCAG.",
    languageNotice:
      "Nodyn: mae cynnwys y datganiad yn cael ei gynhyrchu yn Saesneg ar hyn o bryd. Bydd datganiadau wedi'u cyfieithu ar gael yn fuan.",
    languageSelectorLabel: "Iaith y datganiad",
    accessibilityBadge: "Bathodyn hygyrchedd",
    interactiveWidget: "Teclyn rhyngweithiol",
    fullReport: "Adroddiad llawn",
    dashboard: "Dangosfwrdd",
    runScan: "Rhedeg sgan",
    noStatementYet:
      "Nid oes datganiad wedi'i gynhyrchu eto. Rhedwch sgan a bydd eich datganiad hygyrchedd yn cael ei greu'n awtomatig.",
  },
  sv: {
    title: "Tillgänglighetsredogörelse",
    generatedOn: "Genererad",
    summary: "Sammanfattning",
    wcagLevel: "WCAG-nivå",
    complianceStatus: "Efterlevnadsstatus",
    score: "Poäng",
    manualReview: "Manuell granskning",
    organisation: "Organisation",
    coverage: "Täckning",
    pagesScanned: "Skannade sidor",
    pagesDiscovered: "Upptäckta sidor",
    coveragePercent: "Täckning",
    templatesScanned: "Skannade mallar",
    yourStatement: "Din tillgänglighetsredogörelse",
    copyHtml: "Kopiera HTML",
    copied: "Kopierat!",
    downloadNotice:
      "Tillgänglighetsredogörelse genererad från din senaste skanning. Kopiera HTML-koden och klistra in den på din webbplats.",
    legalDisclaimer:
      "Denna redogörelse speglar endast automatiserade skanningsresultat och är inte en juridisk förklaring om WCAG-efterlevnad.",
    languageNotice:
      "Obs: redogörelsens innehåll genereras för närvarande på engelska. Översatta redogörelser kommer snart.",
    languageSelectorLabel: "Redogörelsens språk",
    accessibilityBadge: "Tillgänglighetsmärke",
    interactiveWidget: "Interaktiv widget",
    fullReport: "Fullständig rapport",
    dashboard: "Instrumentpanel",
    runScan: "Kör skanning",
    noStatementYet:
      "Ingen redogörelse har genererats ännu. Kör en skanning och din tillgänglighetsredogörelse skapas automatiskt.",
  },
  da: {
    title: "Tilgængelighedserklæring",
    generatedOn: "Genereret",
    summary: "Resumé",
    wcagLevel: "WCAG-niveau",
    complianceStatus: "Overholdelsesstatus",
    score: "Score",
    manualReview: "Manuel gennemgang",
    organisation: "Organisation",
    coverage: "Dækning",
    pagesScanned: "Scannede sider",
    pagesDiscovered: "Opdagede sider",
    coveragePercent: "Dækning",
    templatesScanned: "Scannede skabeloner",
    yourStatement: "Din tilgængelighedserklæring",
    copyHtml: "Kopiér HTML",
    copied: "Kopieret!",
    downloadNotice:
      "Tilgængelighedserklæring genereret fra din seneste scanning. Kopiér HTML-koden og indsæt den på din hjemmeside.",
    legalDisclaimer:
      "Denne erklæring afspejler kun automatiserede scanningsresultater og er ikke en juridisk erklæring om WCAG-overensstemmelse.",
    languageNotice:
      "Bemærk: erklæringens indhold genereres i øjeblikket på engelsk. Oversatte erklæringer kommer snart.",
    languageSelectorLabel: "Erklæringens sprog",
    accessibilityBadge: "Tilgængelighedsbadge",
    interactiveWidget: "Interaktiv widget",
    fullReport: "Fuld rapport",
    dashboard: "Dashboard",
    runScan: "Kør scanning",
    noStatementYet:
      "Der er endnu ikke genereret nogen erklæring. Kør en scanning, og din tilgængelighedserklæring oprettes automatisk.",
  },
  pl: {
    title: "Deklaracja dostępności",
    generatedOn: "Wygenerowano",
    summary: "Podsumowanie",
    wcagLevel: "Poziom WCAG",
    complianceStatus: "Status zgodności",
    score: "Wynik",
    manualReview: "Przegląd ręczny",
    organisation: "Organizacja",
    coverage: "Pokrycie",
    pagesScanned: "Przeskanowane strony",
    pagesDiscovered: "Odkryte strony",
    coveragePercent: "Pokrycie",
    templatesScanned: "Przeskanowane szablony",
    yourStatement: "Twoja deklaracja dostępności",
    copyHtml: "Kopiuj HTML",
    copied: "Skopiowano!",
    downloadNotice:
      "Deklaracja dostępności wygenerowana z ostatniego skanowania. Skopiuj HTML i wklej go na swojej stronie internetowej.",
    legalDisclaimer:
      "Niniejsza deklaracja odzwierciedla wyłącznie wyniki automatycznego skanowania i nie stanowi prawnej deklaracji zgodności z WCAG.",
    languageNotice:
      "Uwaga: treść deklaracji jest obecnie generowana w języku angielskim. Przetłumaczone deklaracje będą dostępne wkrótce.",
    languageSelectorLabel: "Język deklaracji",
    accessibilityBadge: "Odznaka dostępności",
    interactiveWidget: "Interaktywny widget",
    fullReport: "Pełny raport",
    dashboard: "Panel",
    runScan: "Uruchom skanowanie",
    noStatementYet:
      "Nie wygenerowano jeszcze żadnej deklaracji. Uruchom skanowanie, a Twoja deklaracja dostępności zostanie utworzona automatycznie.",
  },
};
